import React from 'react';
import { Flex } from 'glints-aries/es';
import { Icon, PrimaryButton, Typography } from 'glints-aries/es/@next';
import { IconNames } from 'glints-aries/es/@next/Icon/icons/icons';
import { borderRadius8 } from 'glints-aries/es/@next/utilities/borderRadius';
import { Blue, Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space4, space16 } from 'glints-aries/es/@next/utilities/spacing';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import styled, { createGlobalStyle } from 'styled-components';

import {
  EmployerPurchaseProductType,
  SubscriptionPlanType,
} from '../../../../common/interfaces/entities';
import { UnverifiedCompanyProductUsageWarningBanner } from '../../SharedComponents/UnverifiedCompanyProductUsageWarningBanner';
import {
  CreditProductPurchaseOrder,
  PremiumPlanPurchaseDetailsType,
  PurchaseOrderPurchaseType,
} from '../../types';
import { StyledButtonGroup } from './styles.sc';
import { TermsAgreementText } from './TermsAgreementModal';

type Props = {
  creditProductPurchaseOrder: Pick<
    CreditProductPurchaseOrder,
    | 'companyProductInventory'
    | 'product'
    | 'purchaseType'
    | 'activeSubscriptionInformation'
    | 'purchaseProductSpecificDetails'
  >;
  onClose: () => void;
};

const ModalStyle = createGlobalStyle`
.modal-content {
  width: 100%;
}
`;
const iconsBasedOnSubscriptionPlan: Record<SubscriptionPlanType, IconNames> = {
  [SubscriptionPlanType.GROWTH]: 'custom-sparkling-fill',
  [SubscriptionPlanType.FREE]: 'ri-shining2-fill',
  [SubscriptionPlanType.SCALE]: 'ri-sparkling2-fill',
  [SubscriptionPlanType.UNLIMITED]: 'ri-infinity',
  [SubscriptionPlanType.CUSTOMIZED]: 'ri-shake-hands-fill',
};

const IconWrapper = styled.div`
  width: 72px;
  height: 72px;
  background-color: ${Blue.S08};
  border-radius: ${borderRadius8};
  box-shadow: 0px 1px 0px 0px rgba(71, 71, 71, 0.1);
  border: 2px solid ${Neutral.B100};
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const IconBasedOnPlanType = (props: {
  subscriptionPlanType: SubscriptionPlanType;
}) => (
  <IconWrapper>
    <Icon
      name={iconsBasedOnSubscriptionPlan[props.subscriptionPlanType]}
      width={36}
      height={36}
      fill={Blue.Brand}
    />
  </IconWrapper>
);

export const translatedPremiumPlanMessages: Record<
  SubscriptionPlanType,
  MessageDescriptor
> = {
  [SubscriptionPlanType.GROWTH]: {
    id: 'text-package-name-growth',
    defaultMessage: 'Growth',
  },
  [SubscriptionPlanType.SCALE]: {
    id: 'text-package-name-scale',
    defaultMessage: 'Scale',
  },
  [SubscriptionPlanType.FREE]: {
    id: 'text-package-name-free',
    defaultMessage: 'Free',
  },
  [SubscriptionPlanType.UNLIMITED]: {
    id: 'text-package-name-enterprise',
    defaultMessage: 'Enterprise',
  },
  [SubscriptionPlanType.CUSTOMIZED]: {
    id: 'text-package-name-customized',
    defaultMessage: 'Customized',
  },
};

function PremiumPlanSuccessModalContent({
  creditProductPurchaseOrder,
  onClose,
}: Props) {
  const {
    product,
    companyProductInventory,
    purchaseType,
    activeSubscriptionInformation,
    purchaseProductSpecificDetails,
  } = creditProductPurchaseOrder;
  const { formatMessage, formatDate } = useIntl();
  if (product.type !== EmployerPurchaseProductType.PREMIUM_PLAN) {
    return null;
  }
  const isPendingPurchase =
    purchaseProductSpecificDetails?.purchaseType ===
    PremiumPlanPurchaseDetailsType.PENDING;

  return (
    <Flex
      flexDirection="column"
      gap={space16}
      alignItems="center"
      style={{
        color: Neutral.B18,
      }}
    >
      <ModalStyle />
      <IconBasedOnPlanType
        subscriptionPlanType={
          product.variantDetail?.planType ?? SubscriptionPlanType.FREE
        }
      />
      <Flex
        flexDirection="column"
        gap={space4}
        alignItems="center"
        style={{ textAlign: 'center' }}
      >
        <Choose>
          <When condition={isPendingPurchase}>
            <Typography variant="body2">
              <FormattedMessage
                id="text-your-plan-will-be-activated"
                defaultMessage="Your plan will be activated after your company is verified."
              />
            </Typography>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="text-you-will-receive-full-access"
                defaultMessage="You’ll receive full access once verification is completed."
              />
            </Typography>
          </When>
          <Otherwise>
            <Typography variant="body2">
              <FormattedMessage
                id="text-your-plan-active"
                defaultMessage="Your {planType} plan is now active!"
                values={{
                  planType: formatMessage(
                    translatedPremiumPlanMessages[
                      product.variantDetail?.planType ??
                        SubscriptionPlanType.FREE
                    ]
                  ),
                }}
              />
            </Typography>
            <Typography variant="subtitle2" color={Neutral.B40}>
              <Choose>
                <When
                  condition={
                    purchaseType === PurchaseOrderPurchaseType.SUBSCRIPTION
                  }
                >
                  {activeSubscriptionInformation?.nextPaymentChargeDate && (
                    <FormattedMessage
                      id="text-plan-auto-renew-on"
                      defaultMessage="The plan will auto renew on {date}"
                      values={{
                        date: (
                          <Typography variant="caption" as="span">
                            {formatDate(
                              activeSubscriptionInformation.nextPaymentChargeDate,
                              {
                                year: 'numeric',
                                month: 'short',
                                day: '2-digit',
                              }
                            )}
                          </Typography>
                        ),
                      }}
                    />
                  )}
                </When>
                <Otherwise>
                  <FormattedMessage
                    id="text-plan-expire-on"
                    defaultMessage="The plan will expire on {expiryDate}."
                    values={{
                      expiryDate: (
                        <Typography variant="caption" as="span">
                          {companyProductInventory?.expiryDateTime
                            ? formatDate(
                                companyProductInventory.expiryDateTime,
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: '2-digit',
                                }
                              )
                            : '-'}
                        </Typography>
                      ),
                    }}
                  />
                </Otherwise>
              </Choose>
            </Typography>
          </Otherwise>
        </Choose>
      </Flex>
      <UnverifiedCompanyProductUsageWarningBanner
        onOpenVerificationModalCallback={onClose}
      />
      <TermsAgreementText />
    </Flex>
  );
}

function ModalTitle({ creditProductPurchaseOrder }: Props) {
  const { purchaseProductSpecificDetails } = creditProductPurchaseOrder;
  const isPendingPurchase =
    purchaseProductSpecificDetails?.purchaseType ===
    PremiumPlanPurchaseDetailsType.PENDING;
  if (isPendingPurchase) {
    return (
      <FormattedMessage
        id="text-subscription-plan-purchased"
        defaultMessage="Subscription Plan Purchased"
      />
    );
  }
  return (
    <FormattedMessage
      id="text-subscription-plan-activated"
      defaultMessage="Subscription Plan Activated"
    />
  );
}

function ModalFooter({ onClose }: { onClose: () => void }) {
  return (
    <StyledButtonGroup>
      <PrimaryButton onClick={onClose}>
        <Typography variant="button">
          <FormattedMessage
            id="interactive-continue"
            defaultMessage="Continue"
          />
        </Typography>
      </PrimaryButton>
    </StyledButtonGroup>
  );
}

export default Object.assign(PremiumPlanSuccessModalContent, {
  ModalTitle,
  ModalFooter,
});
